import { CalendarIcon } from "@chakra-ui/icons";
import {
  CloseButton,
  InputGroup,
  Input,
  InputRightElement,
  InputProps,
  forwardRef,
} from "@chakra-ui/react";
import { formatInTimeZone } from "date-fns-tz";
import React, { useCallback, useState, useEffect } from "react";
import { parseDateInput } from "./parseDateInput";
import { startOfDayInTimeZone } from "./tzDateUtils";

type DatePickerInputProps = {
  /**
   * The selected Date.
   */
  value: Date | undefined;

  onFocus: () => void;

  /**
   * Callback for when a new value is selected / committed
   */
  onSelect: (value: Date | undefined) => void;

  onBlur: () => void;

  placeholder?: string;

  /**
   * If set to false, then this only allows day to be input and displayed, where
   * the resulting date is always set to the start of that day.
   *
   * If set to true, then this allows day and time to be input and displayed.
   */
  showTime?: boolean;

  /**
   * Timezone the user input dates will be interpreted in.
   */
  timeZone: string;

  inputProps?: InputProps;

  isDisabled?: boolean;

  isRequired?: boolean;

  isInvalid?: boolean;
};

/**
 * The text input field for a date picker
 */
export const DatePickerInput = forwardRef<DatePickerInputProps, "input">(
  function DatePickerInput(
    {
      value,
      placeholder = "Select date",
      showTime = false,
      onFocus,
      onBlur,
      onSelect,
      timeZone,
      inputProps,
      isDisabled,
      isRequired,
      isInvalid,
    },
    ref,
  ) {
    const formattedValue = value ? formatDate(value, showTime, timeZone) : "";

    const [inputValue, setInputValue] = useState(formattedValue);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
      if (!isEditing) {
        setInputValue(formattedValue);
      }
    }, [formattedValue, isEditing]);

    const handleSelect = useCallback(
      (input: string | undefined) => {
        if (!input || !input.trim()) {
          onSelect(undefined);
          return;
        }

        const parsed = parseDateInput(input, timeZone);

        if (!parsed) {
          onSelect(undefined);
          return;
        }

        const nextValue =
          showTime ? parsed : startOfDayInTimeZone(parsed, timeZone);

        onSelect(nextValue);
      },
      [onSelect, showTime, timeZone],
    );

    const rightElement =
      value ?
        <InputRightElement>
          <CloseButton
            size="sm"
            aria-label="Clear date"
            onClick={() => {
              handleSelect(undefined);
            }}
          />
        </InputRightElement>
      : <InputRightElement pointerEvents="none">
          <CalendarIcon color="gray.300" />
        </InputRightElement>;

    const inputPlaceholder = showTime ? "DD/MM/YYYY HH:mm:ss" : "DD/MM/YYYY";

    return (
      <InputGroup>
        <Input
          ref={ref}
          {...inputProps}
          isDisabled={isDisabled}
          isRequired={isRequired}
          isInvalid={isInvalid}
          value={inputValue}
          placeholder={isEditing ? inputPlaceholder : placeholder}
          onFocus={() => {
            setIsEditing(true);
            onFocus();
          }}
          onChange={(e) => {
            setInputValue(e.target.value);
          }}
          onBlur={() => {
            setIsEditing(false);
            handleSelect(inputValue);
            onBlur();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              // force blur so focus is lost, otherwise user can't click back in again.
              e.currentTarget.blur();
              return;
            }

            if (e.key === "Escape") {
              e.preventDefault();
              e.currentTarget.blur();
              return;
            }

            if (e.key === "Tab") {
              setIsEditing(false);
              handleSelect(inputValue);
              return;
            }
          }}
          onPaste={(e) => {
            e.preventDefault();

            const pastedText = e.clipboardData.getData("text");
            const nextValue = pastedText.trim();

            if (!nextValue) {
              return;
            }

            setInputValue(nextValue);
            setIsEditing(false);
            handleSelect(nextValue);
          }}
        />

        {rightElement}
      </InputGroup>
    );
  },
);

function formatDate(value: Date, showTime: boolean, timeZone: string): string {
  if (!showTime) {
    return formatInTimeZone(value, timeZone, "dd/MM/yyyy");
  }

  // only show seconds and milliseconds if they are actually set.
  const seconds = formatInTimeZone(value, timeZone, "ss");
  const milliseconds = formatInTimeZone(value, timeZone, "SSS");

  const formatString =
    milliseconds !== "000" ? "dd/MM/yyyy h:mm:ss.SSS a"
    : seconds !== "00" ? "dd/MM/yyyy h:mm:ss a"
    : "dd/MM/yyyy h:mm a";

  return formatInTimeZone(value, timeZone, formatString);
}
